import {
  QuickSetStatusResultSchema,
  type PostAction,
  type QuickSetStatus,
  type QuickSetStatusResult,
} from '@cp/schemas';
import { authFetch } from '../auth/auth-fetch.js';
import type { PublicClientAttachment } from '../approvals/types.js';

export type PostStatus =
  | 'draft_topic'
  | 'topic_pending_client'
  | 'topic_approved'
  | 'content_generating'
  | 'content_review'
  | 'content_pending_client'
  | 'content_approved'
  | 'ready_to_publish'
  | 'published'
  | 'topic_changes_requested'
  | 'content_changes_requested'
  | 'topic_rejected'
  | 'content_rejected'
  | 'scheduled'
  | 'waiting_info';

// The real state-machine action enum. Single source of truth: the shared
// `PostActionSchema` in @cp/schemas, validated by `POST /posts/:id/actions`
// (ApplyActionSchema). The previous local union here was stale/simplified and
// did NOT match the API contract — re-exported from @cp/schemas to stay in sync.
export type { PostAction };

export type BrandOrSales = 'brand' | 'sales';

export interface Post {
  id: string;
  monthlyPlanId: string;
  hotelId: string;
  scheduledFor: string;
  isScheduled: boolean;
  scheduledAt: string | null;
  publishedAt: string | null;
  archivedAt: string | null;
  position: number;
  status: PostStatus;
  /** Where the post stood before it was parked in `waiting_info`; null otherwise. */
  waitingInfoFrom?: PostStatus | null;
  brandOrSales: BrandOrSales;
  category: string | null;
  /** Internal "Paraméter neve"; staff-only. */
  sourceParamName?: string | null;
  publishToFb: boolean;
  publishToIg: boolean;
  selectedTopicTitle?: string | null;
  hasTopicSuggestions?: boolean;
  /** Whether the post has a current content row (any platform). Used by the
   *  platform editor; the status label no longer depends on it. */
  hasContent?: boolean;
  /** When the client last requested changes. Non-null drives the "Módosítást
   *  kért" badge: a per-platform change request leaves the post in
   *  `*_pending_client`, so the status alone would hide the client's reply. */
  clientFeedbackAt?: string | null;
  /** Per-platform client decisions from the latest content batch, only while
   *  the client has answered some platforms but not all — the post stays in
   *  `content_pending_client` until every platform is decided. */
  clientDecisions?: {
    fb: 'approved' | 'changes_requested' | 'rejected' | null;
    ig: 'approved' | 'changes_requested' | 'rejected' | null;
    pending: ('fb' | 'ig')[];
  } | null;
  createdAt: string;
  updatedAt: string;
}

export interface CreatePostBody {
  scheduledFor: string;
  brandOrSales: BrandOrSales;
  category?: string;
  publishToFb?: boolean;
  publishToIg?: boolean;
}

export interface UpdatePostBody {
  scheduledFor?: string;
  brandOrSales?: BrandOrSales;
  category?: string | null;
  sourceParamName?: string | null;
  publishToFb?: boolean;
  publishToIg?: boolean;
  isScheduled?: boolean;
}

export interface RotationWarning {
  postId: string;
  reason: string;
  severity: 'info' | 'warn' | 'danger';
  category?: string | null;
  conflictsWith?: string[];
}

export interface PostTopic {
  id: string;
  postId: string;
  title: string;
  angle: string | null;
  isSelected: boolean;
  generatedBy: 'ai' | 'manual' | 'ai_edited';
  aiTaskId: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface CreatePostTopicBody {
  title: string;
  angle?: string;
  rationale?: string;
}

export interface PostContent {
  id: string;
  postId: string;
  platform: 'fb' | 'ig';
  version: number;
  body: string;
  hashtags: string[];
  sourceUrl: string | null;
  /** Extra link under the text (pl. videó), per platform. */
  attachmentUrl?: string | null;
  isCurrent?: boolean;
  generatedBy?: 'ai' | 'manual' | 'ai_edited';
  aiTaskId?: string | null;
  createdAt: string;
  updatedAt?: string;
}

export interface UpsertPostContentBody {
  platform: 'fb' | 'ig';
  body: string;
  hashtags?: string[];
  sourceUrl?: string;
  attachmentUrl?: string;
}

export interface PostImage {
  id: string;
  postId: string;
  platform: 'fb' | 'ig';
  hotelMediaId: string;
  order: number;
  url: string;
  /**
   * Non-null when this attachment carries an admin waiver (the API's
   * `override_reason`). The editor reads it to know that a blocked picture
   * already HAS a standing waiver, so a plain re-save — removing some other
   * image — must not demand a fresh one that a non-admin could never give.
   */
  overrideReason: string | null;
}

export interface StaffClientAttachment extends PublicClientAttachment {
  promotedMediaId: string | null;
}

export interface Comment {
  id: string;
  postId: string;
  body: string;
  kind: 'internal' | 'client';
  authorId: string | null;
  authorName: string | null;
  createdAt: string;
  /** When the client was e-mailed about this staff reply. An older API omits it. */
  clientNotifiedAt?: string | null;
  attachments?: StaffClientAttachment[];
}

/** JWT image fetch: an <img> cannot send the Authorization header. */
export async function fetchClientAttachmentBlob(postId: string, id: string): Promise<Blob> {
  const res = await authFetch(`/posts/${postId}/client-attachments/${id}`);
  if (!res.ok) throw new ApiError('A kép nem tölthető be.', res.status);
  return res.blob();
}

export async function promoteClientAttachment(
  postId: string,
  id: string,
): Promise<{ mediaId: string }> {
  const res = await authFetch(`/posts/${postId}/client-attachments/${id}/promote`, {
    method: 'POST',
  });
  return jsonOrThrow<{ mediaId: string }>(res, 'Az átvétel nem sikerült.');
}

/**
 * Error thrown for a non-2xx API response. Carries the problem+json `title`
 * as `message`, plus the zod `fieldErrors` map when the API's
 * ZodValidationPipe rejected the body — so a caller can tell the user WHICH
 * field failed instead of a bare "Mentési hiba".
 */
export class ApiError extends Error {
  constructor(
    message: string,
    readonly status: number,
    readonly fieldErrors: Record<string, string[]> | null = null,
    /** Machine-readable `code` from the error body, when the API sends one. */
    readonly code: string | null = null,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

export async function jsonOrThrow<T>(res: Response, errMsg: string): Promise<T> {
  if (!res.ok) {
    let title = errMsg;
    let fieldErrors: Record<string, string[]> | null = null;
    let code: string | null = null;
    try {
      const body = (await res.json()) as {
        title?: string;
        code?: unknown;
        errors?: { fieldErrors?: Record<string, string[]> };
      };
      if (body.title) title = body.title;
      if (body.errors?.fieldErrors) fieldErrors = body.errors.fieldErrors;
      if (typeof body.code === 'string') code = body.code;
    } catch {
      /* ignore */
    }
    throw new ApiError(title, res.status, fieldErrors, code);
  }
  return (await res.json()) as T;
}

export async function listPosts(planId: string): Promise<Post[]> {
  const res = await authFetch(`/plans/${planId}/posts`);
  return jsonOrThrow<Post[]>(res, 'failed to list posts');
}

export async function rotationWarnings(planId: string): Promise<RotationWarning[]> {
  const res = await authFetch(`/plans/${planId}/rotation-warnings`);
  return jsonOrThrow<RotationWarning[]>(res, 'failed to load rotation warnings');
}

export async function createSlot(planId: string, body: CreatePostBody): Promise<Post> {
  const res = await authFetch(`/plans/${planId}/posts`, {
    method: 'POST',
    body: JSON.stringify(body),
  });
  return jsonOrThrow<Post>(res, 'failed to create slot');
}

export async function autoGenerate(planId: string, count?: number): Promise<Post[]> {
  const res = await authFetch(`/plans/${planId}/posts/auto-generate`, {
    method: 'POST',
    body: JSON.stringify(count !== undefined ? { count } : {}),
  });
  return jsonOrThrow<Post[]>(res, 'failed to auto-generate');
}

export async function getPost(id: string): Promise<Post> {
  const res = await authFetch(`/posts/${id}`);
  return jsonOrThrow<Post>(res, 'failed to fetch post');
}

export async function updatePost(id: string, body: UpdatePostBody): Promise<Post> {
  const res = await authFetch(`/posts/${id}`, { method: 'PATCH', body: JSON.stringify(body) });
  return jsonOrThrow<Post>(res, 'failed to update post');
}

export async function deletePost(id: string): Promise<void> {
  const res = await authFetch(`/posts/${id}`, { method: 'DELETE' });
  if (!res.ok) throw new Error('failed to delete post');
}

export async function applyAction(id: string, action: PostAction, comment?: string): Promise<Post> {
  const body = comment !== undefined ? { action, comment } : { action };
  const res = await authFetch(`/posts/${id}/actions`, {
    method: 'POST',
    body: JSON.stringify(body),
  });
  return jsonOrThrow<Post>(res, 'transition failed');
}

// Statuses a manager can set directly via the override switcher. Excludes the
// two *_pending_client states (reaching "ügyfélnél" goes through the submit
// actions, which also email the client) and the transient content_generating.
export type ManagerSettableStatus =
  | 'draft_topic'
  | 'topic_approved'
  | 'content_review'
  | 'content_approved'
  | 'ready_to_publish'
  | 'published'
  | 'topic_changes_requested'
  | 'content_changes_requested'
  | 'topic_rejected'
  | 'content_rejected'
  | 'scheduled'
  | 'waiting_info';

export interface SetPostStatusBody {
  status: ManagerSettableStatus;
  note?: string;
  source?: 'phone' | 'email' | 'other';
  notifyClient?: boolean;
  clientEmail?: string;
  /** Required when status === 'published'. ISO datetime. */
  publishedAt?: string;
  /** Required (true) to walk a published post back to an earlier state. */
  confirmRepublish?: boolean;
}

export async function setPostStatus(id: string, body: SetPostStatusBody): Promise<Post> {
  const res = await authFetch(`/posts/${id}/status`, {
    method: 'PATCH',
    body: JSON.stringify(body),
  });
  return jsonOrThrow<Post>(res, 'failed to set status');
}

/** One-click status change; the API answers with the `previous*` values an undo needs. */
export async function quickSetStatus(
  id: string,
  body: QuickSetStatus,
): Promise<QuickSetStatusResult> {
  const res = await authFetch(`/posts/${id}/quick-status`, {
    method: 'POST',
    body: JSON.stringify(body),
  });
  return QuickSetStatusResultSchema.parse(await jsonOrThrow<unknown>(res, 'quick status failed'));
}

export interface MovePostBody {
  targetPlanId: string;
  newScheduledFor: string;
}

export async function movePost(id: string, body: MovePostBody): Promise<Post> {
  const res = await authFetch(`/posts/${id}/move`, {
    method: 'POST',
    body: JSON.stringify(body),
  });
  return jsonOrThrow<Post>(res, 'failed to move post');
}

export async function listTopics(postId: string): Promise<PostTopic[]> {
  const res = await authFetch(`/posts/${postId}/topics`);
  return jsonOrThrow<PostTopic[]>(res, 'failed to list topics');
}

export async function createTopic(postId: string, body: CreatePostTopicBody): Promise<PostTopic> {
  const res = await authFetch(`/posts/${postId}/topics`, {
    method: 'POST',
    body: JSON.stringify(body),
  });
  return jsonOrThrow<PostTopic>(res, 'failed to create topic');
}

export async function triggerPostTopic(
  postId: string,
  instruction?: string,
): Promise<{ postId: string; jobId: string }> {
  const trimmed = instruction?.trim();
  const res = await authFetch(`/posts/${postId}/trigger-topic`, {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify(trimmed ? { instruction: trimmed } : {}),
  });
  return jsonOrThrow<{ postId: string; jobId: string }>(res, 'failed to trigger topic AI');
}

export async function regenerateCopy(
  postId: string,
  body: {
    platform: 'fb' | 'ig';
    tone?: string;
    fieldOnly?: 'all' | 'body' | 'hashtags';
    length?: 'short' | 'long';
  },
): Promise<{ postId: string; platform: 'fb' | 'ig'; jobId: string }> {
  const res = await authFetch(`/posts/${postId}/regenerate-copy`, {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify(body),
  });
  return jsonOrThrow<{ postId: string; platform: 'fb' | 'ig'; jobId: string }>(
    res,
    'failed to regenerate copy',
  );
}

export async function updateTopic(
  id: string,
  body: { title?: string; angle?: string | null },
): Promise<PostTopic> {
  const res = await authFetch(`/topics/${id}`, {
    method: 'PATCH',
    body: JSON.stringify(body),
  });
  return jsonOrThrow<PostTopic>(res, 'failed to update topic');
}

export async function selectTopic(id: string): Promise<PostTopic> {
  const res = await authFetch(`/topics/${id}/select`, { method: 'POST' });
  return jsonOrThrow<PostTopic>(res, 'failed to select topic');
}

export async function listContents(postId: string): Promise<PostContent[]> {
  const res = await authFetch(`/posts/${postId}/contents`);
  return jsonOrThrow<PostContent[]>(res, 'failed to list contents');
}

export async function upsertContent(
  postId: string,
  body: UpsertPostContentBody,
): Promise<PostContent> {
  const res = await authFetch(`/posts/${postId}/contents`, {
    method: 'POST',
    body: JSON.stringify(body),
  });
  return jsonOrThrow<PostContent>(res, 'failed to upsert content');
}

export async function listContentHistory(
  postId: string,
  platform: 'fb' | 'ig',
): Promise<PostContent[]> {
  const res = await authFetch(`/posts/${postId}/contents/history?platform=${platform}`);
  return jsonOrThrow<PostContent[]>(res, 'failed to load content history');
}

export async function listImages(postId: string, platform: 'fb' | 'ig'): Promise<PostImage[]> {
  const res = await authFetch(`/posts/${postId}/images?platform=${platform}`);
  return jsonOrThrow<PostImage[]>(res, 'failed to list images');
}

/**
 * Admin waiver for the fail-closed attach gate — mirrors the API's
 * `AttachOverrideSchema`. Omitted for an ordinary attach; the server refuses
 * it outright for a non-admin, and demands `note` when a sensitive tag is on
 * the picture without confirmed consent.
 */
export interface AttachOverride {
  reason: 'rights_verified_offline' | 'consent_verified_offline' | 'other';
  note?: string;
}

export async function setImages(
  postId: string,
  platform: 'fb' | 'ig',
  hotelMediaIds: string[],
  override?: AttachOverride,
): Promise<PostImage[]> {
  const res = await authFetch(`/posts/${postId}/images`, {
    method: 'POST',
    // The key is omitted entirely when there is no waiver — an explicit
    // `override: undefined` would serialise away anyway, but leaving it out
    // keeps the request body honest about what was asked for.
    body: JSON.stringify(
      override ? { platform, hotelMediaIds, override } : { platform, hotelMediaIds },
    ),
  });
  return jsonOrThrow<PostImage[]>(res, 'failed to set images');
}

/**
 * Copy one platform's current image set onto the other, server-side — carries
 * uploads and AI-generated pictures too, which `setImages` (library refs only)
 * cannot express. The target's previous set is demoted, not deleted.
 */
export async function copyImages(
  postId: string,
  from: 'fb' | 'ig',
  to: 'fb' | 'ig',
): Promise<PostImage[]> {
  const res = await authFetch(`/posts/${postId}/images/copy`, {
    method: 'POST',
    body: JSON.stringify({ from, to }),
  });
  return jsonOrThrow<PostImage[]>(res, 'failed to copy images');
}

export async function reorderImages(
  postId: string,
  platform: 'fb' | 'ig',
  hotelMediaIds: string[],
): Promise<PostImage[]> {
  const res = await authFetch(`/posts/${postId}/images/reorder`, {
    method: 'PATCH',
    body: JSON.stringify({ platform, hotelMediaIds }),
  });
  return jsonOrThrow<PostImage[]>(res, 'failed to reorder images');
}

export async function listComments(
  postId: string,
  kind: 'internal' | 'client',
): Promise<Comment[]> {
  const res = await authFetch(`/posts/${postId}/comments?kind=${kind}`);
  return jsonOrThrow<Comment[]>(res, 'failed to list comments');
}

export async function createComment(
  postId: string,
  body: { body: string; kind: 'internal' | 'client' },
): Promise<Comment> {
  const res = await authFetch(`/posts/${postId}/comments`, {
    method: 'POST',
    body: JSON.stringify(body),
  });
  return jsonOrThrow<Comment>(res, 'failed to create comment');
}
